package er.prateek.crossly.ui.components

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.common_error_generic
import crossly.shared.generated.resources.common_loading
import crossly.shared.generated.resources.common_retry
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import org.jetbrains.compose.resources.stringResource

/**
 * On-brand loader: a 3x3 crossword block where squares light up in sequence.
 * Reads as "a puzzle is being laid out" rather than a generic Material spinner.
 */
@Composable
fun PuzzleLoader(modifier: Modifier = Modifier, size: androidx.compose.ui.unit.Dp = 56.dp) {
    val colors = MaterialTheme.game
    val transition = rememberInfiniteTransition(label = "loader")
    val phase = transition.animateFloat(
        initialValue = 0f,
        targetValue = 9f,
        animationSpec = infiniteRepeatable(tween(1400, easing = LinearEasing)),
        label = "loaderPhase",
    ).value

    Canvas(modifier = modifier.size(size)) {
        val gap = this.size.minDimension * 0.06f
        val cell = (this.size.minDimension - gap * 2f) / 3f
        repeat(9) { index ->
            val row = index / 3
            val col = index % 3
            // Distance around the cycle drives brightness, so the lit square
            // sweeps rather than blinking on and off.
            val distance = ((phase - index) + 9f) % 9f
            val intensity = (1f - distance / 3f).coerceIn(0f, 1f)
            drawRoundRect(
                color = colors.accent.copy(alpha = 0.18f + intensity * 0.82f),
                topLeft = Offset(col * (cell + gap), row * (cell + gap)),
                size = Size(cell, cell),
                cornerRadius = CornerRadius(cell * 0.22f, cell * 0.22f),
            )
        }
    }
}

@Composable
fun LoadingState(modifier: Modifier = Modifier) {
    val colors = MaterialTheme.game
    Column(
        modifier = modifier.fillMaxSize(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        PuzzleLoader()
        Text(
            text = stringResource(Res.string.common_loading),
            style = MaterialTheme.typography.labelLarge,
            color = colors.inkMuted,
            modifier = Modifier.padding(top = Spacing.lg),
        )
    }
}

@Composable
fun ErrorState(
    modifier: Modifier = Modifier,
    message: String = stringResource(Res.string.common_error_generic),
    onRetry: (() -> Unit)? = null,
) {
    val colors = MaterialTheme.game
    Column(
        modifier = modifier.fillMaxSize().padding(Spacing.xl),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyLarge,
            color = colors.ink,
            textAlign = TextAlign.Center,
        )
        onRetry?.let {
            GameButton(
                text = stringResource(Res.string.common_retry),
                onClick = it,
                style = GameButtonStyle.PRIMARY,
                modifier = Modifier.padding(top = Spacing.lg),
            )
        }
    }
}
